import React, {useContext, useEffect, useState} from 'react';
import Row from 'antd/lib/grid/row';
import Col from 'antd/lib/grid/col';
import {FormContext} from '../metadata/MDForm';
import ls from './Questions.less';
import {YesNo} from './YesNo';
import {YearPicker} from './YearPicker';
import {Checkbox, DatePicker, Input, InputNumber} from 'antd';
import { AsYouType } from 'libphonenumber-js'
import {dateToString, stringToDate} from '../../utils/dateUtils';
import {ProductInfoContext} from '../data/ApplicationDataSource';
import {merge} from 'lodash';
import {BASIC_ELEMENT_SCHEMA} from '../layout/Group';
import {ModelContext} from '../metadata/ModelSource';
import { GlobalStateContext } from '../../context/GlobalStateProvider';
import { FormValidationContext } from '../../context/FormValidationProvider';
import { getQuestionsForProduct, getQuestionsForProductRisk, getQuestionsForDriver } from '../../api/applicationAPI';
import {coerceToBoolean} from '../metadata/coercer';
import {PortalSelect, PortalSelectOption} from '../PortalSelect';

export function Questions({forTypeCd, lineCd, riskTypeCd, partyTypeCd, applicationStateCd, fieldOverrides=[]}) {
  const applicationModel = useContext(ModelContext);
  const productInfoContext = useContext(ProductInfoContext);
  const { getValue, setValue, productSetupContext, prefix, validateAttribute } = useContext(FormContext);
  const formValidationContext = useContext(FormValidationContext);
  const appTypeCd = applicationModel.getValue('typeCd');
  const typeCd = forTypeCd || appTypeCd;
  let [ questions, setQuestions ] = useState({});
  let [ displayed, setDisplayed ] = useState(false);

  /**
   * Questions are retrieved from the API level that preprocesses (Velocity)
   * and filters.  When the API fails, provide questions from productSetup
   * without processing.  Apply filters here.
   * @param questionTypeCd
   * @returns {Promise<null>}  for questions structure, with question[ ] member
   */
  const getQuestions = async (questionTypeCd) => {
    let result = null;

    if ((questionTypeCd === 'Quote') || (questionTypeCd === 'Application')) {
      let data = await getQuestionsForProduct(applicationModel.data.systemId, applicationStateCd);
      if (data && data.question) result = data;
      if ( ! result) {
        // questions for the product level come from the product info
        result = filterType(productInfoContext.productSetup.questions[0], questionTypeCd);
        console.log(`Unable to get dynamic questions for ${questionTypeCd}, falling back to static questions`);
      }
    } else if (questionTypeCd === 'Risk') {
      let data = await getQuestionsForProductRisk(applicationModel.data.systemId, lineCd, riskTypeCd, applicationStateCd);
      if (data && data.question) result = data;
      if ( ! result) {
        // questions for line/risk come from the product setup context (set up in MDForm)
        result = filterType(productSetupContext.questions[0], appTypeCd);
        console.log(`Unable to get dynamic questions for ${questionTypeCd}, falling back to static questions`);
      }
    } else if (questionTypeCd === 'Driver') {
      let data = await getQuestionsForDriver(applicationModel.data.systemId, partyTypeCd, applicationStateCd);
      if (data && data.question) result = data;
      if ( ! result) {
        // questions for driver come from the product setup context (set up in MDForm)
        result = filterType(productSetupContext.questions[0], appTypeCd);
        console.log(`Unable to get dynamic questions for ${questionTypeCd}, falling back to static questions`);
      }
    }
    if (result) { console.log(`Loaded ${result.question.length} questions for type ${questionTypeCd}`); } else { console.log(`Unable to find any questions for ${questionTypeCd}`); }
    return (result);
  }

  useEffect(( ) => {
    const initialStatuses = {};

    if (questions && questions['question']) {
      questions.question.map((question) => {
        const onChange = v => onReply(question.name, v&&v.target ? v.target.value : v);
        let value = replies[question.name];
        if (value === undefined) {
          value = question.defaultValue;
        }
        value = value || '';

        onChange(value);

        initialStatuses[question.name] = {
          touched: false,
          errors: validateAttribute(question, value)
        }
      });
      formValidationContext.setFieldStatuses(prefix, initialStatuses);
      if ( ! displayed) setDisplayed(true);  // force a third pass through render to capture these cahnges
    } else {
      getQuestions(typeCd).then((result) => {
        setQuestions(result);
        setDisplayed(false);
        /*if (!questions) return;  👍 was return(null)*/
      });
    }

    return () => formValidationContext.removeForm(prefix);
  }, [ questions, displayed ]);

  function filterType(questions, typeCd) {
    let result = [ ];

    for (const q of questions.question) {
      if (( ! q.statusList) || q.statusList.includes(typeCd)) result.push(q);
    }
    return ({...questions, question: result });  // replace the question list by the filtered list
  }

  function onReply(name, value) {
    setValue(`questionReplies.questionReply{name:${name}}.value`, value);
    formValidationContext.setFieldStatuses(prefix, {[name]: {touched: true}});
  }

  const replies = {};

  const QuestionReplies = getValue('questionReplies');
  if (QuestionReplies && QuestionReplies.questionReply) {
    QuestionReplies.questionReply.forEach(r => replies[r.name] = r.value);
  }
  const isReady = (questions && ('question' in questions) && (questions.question.length > 0));
  return (isReady
      ? <QuestionsImpl formValidationContext={formValidationContext} prefix={prefix} questions={questions}
                       replies={replies} onReply={onReply} typeCd={typeCd} fieldOverrides={fieldOverrides}/>
      : <div>There are no questions for this application.</div>);
}

Questions.schema = merge({
  properties: {
    forTypeCd: {
      type: 'string',
    },
  }
}, BASIC_ELEMENT_SCHEMA);

export function QuestionsImpl({questions, replies, onReply, typeCd, fieldOverrides}) {
  return <div className={ls.questions}>
    {questions.question
      .map(q => <Question key={q.id} replies={replies} question={q} onReply={onReply} typeCd={typeCd} fieldOverrides={fieldOverrides}/>)}
  </div>
}

function Question({question, replies, onReply, typeCd, fieldOverrides}) {
  const Component = getComponentForType(question.dataType);
  const { isDesktop } = useContext(GlobalStateContext);
  let value = replies[question.name];
  if (value === undefined) {
    value = question.defaultValue;
  }
  value = value || '';
  const onChange = v => onReply(question.name, v&&v.target ? v.target.value : v);


  // Get sub questions showOnParentValue value and then compare to current question value
  // to determine whether sub questions should show. This is temporary solution only working
  // when sub questions have same showOnParentValue.
  let showOnParentValue = 'Yes';
  if (question.questions){
    for (let q of question.questions.question){
      if (q.showOnParentValue){
        showOnParentValue = q.showOnParentValue;
        break;
      }
    }
  }

  const required = coerceToBoolean(question.requiredInd);
  const isLabel = question.dataType === 'Label';
  return <React.Fragment>
    <Row gutter={[16, 16]}>
      <Col span={isDesktop ? 16 : 24} className={isLabel ? `${ls.questionsLabel}` : ''}>
        {question.text + (required && isLabel ? '*' : '')}
      </Col>
      {isSelectType(question.dataType) ? (
        <ComponentType isDesktop={isDesktop} value={value} onChange={onChange} question={question}/>
      ) : (
        <Col span={isDesktop ? 8 : 24} style={{display: 'flex', justifyContent: 'flex-end', marginBottom: isDesktop ? '' : '20px'}}>
          <Component defaultValue={value} onChange={onChange} question={question}/>
        </Col>
      )}
    </Row>
    {question.questions && value && value === showOnParentValue && <QuestionsImpl questions={question.questions} replies={replies} onReply={onReply} typeCd={typeCd} fieldOverrides={fieldOverrides}/>}
  </React.Fragment>;
}

function getComponentForType(dataType) {

  if (dataType === 'TextArea') {
    return TextArea;
  } else if (dataType === 'Text') {
    return Text;
  } else if (dataType === 'Numeric') {
    return Numeric;
  } else if (dataType === 'Currency') {
    return Currency;
  } else if (dataType === 'Percentage') {
    return Percentage;
  } else if (dataType === 'Phone') {
    return Phone;
  } else if (dataType === 'SelectList') {
    return PortalSelect;
  } else if (dataType === 'ListBox') {
    return PortalSelect;
  } else if (dataType === 'List') {
    return PortalSelect;
  } else if (dataType === 'RadioButtonGroup') {
    return Text;
  } else if (dataType === 'CheckBox') {
    return CheckBox;
  } else if (dataType === 'YesNo') {
    return YesNo;
  } else if (dataType === 'Yes') {
    return Yes;
  } else if (dataType === 'Date') {
    return QDatePicker;
  } else if (dataType === 'Year') {
    return YearPicker;
  } else if (dataType === 'Label') {
    return Label;
  } else {
    return Text;
  }

}

export function TextArea({defaultValue, onChange, question}) {
  return <Input.TextArea aria-label="Text input" defaultValue={defaultValue} onBlur={onChange}
                         rows={question.Height || '4'} cols={question.Width || '50'}
  />
}

export function Text({defaultValue, onChange}) {
  return <Input defaultValue={defaultValue} onBlur={onChange} />
}

function parseFloatNoNaN(value) {
  let float = parseFloat(value);
  if (float !== float) {
    return 0.0;
  }
  return float;
}

export function Numeric({defaultValue, onChange, question}) {
  const decimalPlaces = parseInt(question.DecimalPlaces);
  if (!decimalPlaces) {
    return <InputNumber defaultValue={defaultValue} onBlur={onChange} />;
  }
  return <InputNumber
    defaultValue={defaultValue}
    formatter={value => {
        if (!value && value !== 0) {
          return '';
        }
        return (parseFloatNoNaN(value+'').toFixed(decimalPlaces))||'' + ''
      }
    }
    parser={value => value}
    onBlur={onChange}
  />;
}

export function Currency({defaultValue, onChange, question}) {

  const decimalPlaces = parseInt(question.DecimalPlaces) || 0;

  return <InputNumber
    defaultValue={defaultValue}
    formatter={value => {
        if (!value && value !== 0) {
          return '';
        }
        return '$' + parseFloatNoNaN(value).toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
    }
    parser={value => value.replace(/\$\s?|(,*)/g, '')}
    onBlur={onChange}
  />;
}

export function Percentage({defaultValue, onChange}) {

  return  <InputNumber
    defaultValue={defaultValue}
    min={0}
    max={100}
    formatter={value => `${value}%`}
    parser={value => value.replace('%', '')}
    onBlur={onChange}
  />;
}

export function Phone({defaultValue, onChange}) {

  let [value, setValue] = useState(defaultValue);

  function formatter(str) {
    return new AsYouType('US').input(str);
  }

  return  <Input
    value={value}
    onChange={e => setValue(formatter(e.target.value))}
    onBlur={onChange}
  />;
}

export function CheckBox({defaultValue, onChange}) {
  const { isDesktop } = useContext(GlobalStateContext)
  return <Checkbox defaultChecked={defaultValue === 'Yes'} style={{marginRight:isDesktop ? '200px' : ''}} onChange={e => onChange(e.target.checked ? 'Yes' : 'No')} />;
}

export function QDatePicker({defaultValue, onChange}) {
  return <DatePicker defaultValue={defaultValue ? stringToDate(defaultValue):null} onChange={v => onChange(v ? dateToString(v): undefined)}/> ;
}

export function Label({defaultValue}) {
  return <span>{defaultValue}</span>;
}

function isSelectType(dataType) {
  if (dataType === 'SelectList' || dataType === 'ListBox' || dataType === 'List') {
    return true;
  }
  return false;
}

function ComponentType({ isDesktop, value, onChange, question }) {
  const selectOptions = question.constraints && question.constraints.length ? question.constraints.split(',').map((option) => {
    return {
      value: option,
      name: option,
    };
  }) : [];

  return (
      <Col span={isDesktop ? 8 : 24} style={{marginBottom: isDesktop ? '' : '20px'}}>
        <PortalSelect value={value} onChange={onChange} question={question}>
          {
            selectOptions.map(({value, name}) => <PortalSelectOption value={value}>
              {name}
            </PortalSelectOption>)
          }
        </PortalSelect>
      </Col>
  )
}

